import type React from 'react';
import { useCallback, useEffect, useRef } from 'react';

import type { VirtualItem } from '@tanstack/react-virtual';
import { useVirtualizer } from '@tanstack/react-virtual';
import { twMerge } from 'tailwind-merge';

import { Spinner } from '@/_components';

import { QuickAddContainer } from './quick-add-container';
import type { QuickAddContainerProps } from './types';

import { NoDataMessage } from '../no-data-message';

type VirtualizedInfiniteContainerProps<T> = Omit<QuickAddContainerProps, 'children'> & {
  data: T[];
  loadedPages: number[];
  emptyMessage: string;
  estimatedSize?: number | ((index: number) => number);
  hasMore: boolean;
  fetchMore: () => void;
  isLoading: boolean;
  Wrapper?: (children: React.ReactNode) => React.ReactNode;
  renderRow: (item: T) => React.ReactNode;
  totalSizeCorrection?: number;
};

export function VirtualizedInfiniteContainer<T>({
  data,
  loadedPages,
  emptyMessage,
  estimatedSize = 45,
  hasMore,
  isLoading,
  fetchMore,
  Wrapper,
  renderRow,
  contentClassName,
  totalSizeCorrection = 0,
  ...containerProps
}: VirtualizedInfiniteContainerProps<T>) {
  const fetchSentRef = useRef<Record<number, boolean>>({});

  const parentRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: hasMore ? data.length + 1 : data.length,
    getScrollElement: () => parentRef.current,
    estimateSize: typeof estimatedSize === 'function' ? estimatedSize : () => estimatedSize,
  });

  const virtualizedItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize() - totalSizeCorrection;
  const isEmpty = data.length === 0;

  const renderVirtualItem = useCallback(
    (virtualItem: VirtualItem, renderItem: VirtualizedInfiniteContainerProps<T>['renderRow']) => {
      const isLoaderRow = virtualItem.index > data.length - 1;
      const item = data[virtualItem.index];

      return (
        <div key={virtualItem.key} data-index={virtualItem.index} ref={virtualizer.measureElement}>
          <div className={twMerge('w-full', !isLoading && isLoaderRow && 'hidden')}>
            {isLoaderRow ? (
              <div className="flex h-10 w-full items-center justify-center">
                <Spinner size="xs" />
              </div>
            ) : (
              renderItem(item)
            )}
          </div>
        </div>
      );
    },
    [data, isLoading, virtualizer.measureElement]
  );

  const renderContent = useCallback(() => {
    if (isEmpty) {
      return (
        <div className="3xl:pr-8 pr-6">
          <NoDataMessage message={emptyMessage} />
        </div>
      );
    }
    const renderList = () => virtualizedItems.map((value) => renderVirtualItem(value, renderRow));

    return (
      <div
        className="relative w-full"
        style={{
          height: `${totalSize}px`,
        }}
      >
        <div
          className={twMerge('3xl:pr-6 absolute top-0 left-0 w-full pr-4', contentClassName)}
          style={{
            transform: `translateY(${virtualizedItems[0]?.start ?? 0}px)`,
          }}
        >
          {Wrapper ? Wrapper(<>{renderList()}</>) : renderList()}
        </div>
      </div>
    );
  }, [Wrapper, contentClassName, emptyMessage, isEmpty, renderRow, renderVirtualItem, totalSize, virtualizedItems]);

  useEffect(() => {
    const lastItem = virtualizer.getVirtualItems().at(-1);

    if (!lastItem) {
      return;
    }

    if (parentRef.current) {
      const lastPage = loadedPages[loadedPages.length - 1];
      if (
        !fetchSentRef.current[lastPage + 1] &&
        lastItem.index >= data.length - 1 &&
        hasMore &&
        !isLoading &&
        parentRef.current.scrollTop > 5
      ) {
        fetchSentRef.current[lastPage + 1] = true;
        fetchMore();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, fetchMore, parentRef, data, isLoading, loadedPages, virtualizer.getVirtualItems()]);

  return (
    <QuickAddContainer ref={parentRef} {...containerProps}>
      {renderContent()}
    </QuickAddContainer>
  );
}
